import React, { useEffect, useMemo, useState } from "react"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import { faBoxOpen, faClose, faLayerGroup, faPlus, faSliders, faTrashCan } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../Animate/Animate"
import Button, { ButtonIcon } from "../Button/Button"
import { CountElements } from "../CountElements/CountElements"
import { GenericDropDown } from "../Dropdown/Dropdown"
import { PopupHelpButton } from "../Help/PopupHelpButton"
import { ListSelections } from "../ListSelections/ListSelections"
import ModalPortal from "../Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../Titles/Titles"
import { SIZE } from "../../configuration/Sizes"
import THEME from "../../configuration/Theme"
import { useGetCatalogItems } from "../../hooks/CatalogItems/useGetCatalogItems"
import { useGetCombo } from "../../hooks/Combo/useGetCombo"
import { useGetProducts } from "../../hooks/Products/useGetProducts"
import { catalogItemsMemory, comboMemory, productsMemory } from "../../Objects/Memory/Memory"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import GetSpace from "../../utils/space"

export interface ProductAssociationRequest {
  targetType: number
  productIds: number[]
  catalogItemIds: number[]
  comboIds: number[]
}

export interface ProductAssociationTargetValues {
  allProducts: number
  selectedProducts: number
  allCatalogItems: number
  selectedCatalogItems: number
  allCombos: number
  selectedCombos: number
}

export interface ProductAssociationLabels {
  targetLegend: string
  allProducts: string
  selectedProducts: string
  allCatalogItems: string
  selectedCatalogItems: string
  allCombos: string
  selectedCombos: string
  selectProductsAction: string
  selectCatalogItemsAction: string
  selectCombosAction: string
  selectedProductsCount: string
  selectedCatalogItemsCount: string
  selectedCombosCount: string
  selectedProductsTitle: string
  selectedCatalogItemsTitle: string
  selectedCombosTitle: string
  noSelectedProducts: string
  noSelectedCatalogItems: string
  noSelectedCombos: string
}

export interface ProductAssociationInteractionKeys {
  closeDescriptionKey: string
  targetDescriptionKey: string
  confirmDescriptionKey: string
  addProductDescriptionKey: string
  removeProductDescriptionKey: string
  addCatalogItemDescriptionKey: string
  removeCatalogItemDescriptionKey: string
  addComboDescriptionKey: string
  removeComboDescriptionKey: string
}

interface ProductAssociationApplyPopupProps {
  title: string
  actionLabel: string
  helpTitle: string
  helpDescription: string
  helpBullets: string[]
  helpKey: string
  labels: ProductAssociationLabels
  targetValues: ProductAssociationTargetValues
  interactionKeys: ProductAssociationInteractionKeys
  onClose: () => void
  onConfirm: (request: ProductAssociationRequest) => Promise<void>
}

type OptionValue =
  | "all_products"
  | "selected_products"
  | "all_catalog_items"
  | "selected_catalog_items"
  | "all_combos"
  | "selected_combos"

export const ProductAssociationApplyPopup: React.FC<ProductAssociationApplyPopupProps> = ({
  title,
  actionLabel,
  helpTitle,
  helpDescription,
  helpBullets,
  helpKey,
  labels,
  targetValues,
  interactionKeys,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation(["buttons", "list"])

  const { handleGetProducts } = useGetProducts()

  const { handleGetCatalogItems } = useGetCatalogItems()

  const { handleGetCombo } = useGetCombo()

  const [targetType, setTargetType] = useState<OptionValue>("all_products")

  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([])

  const [selectedCatalogItemIds, setSelectedCatalogItemIds] = useState<number[]>([])

  const [selectedComboIds, setSelectedComboIds] = useState<number[]>([])

  const [enableProductsSelection, setEnableProductsSelection] = useState(false)

  const [enableCatalogItemsSelection, setEnableCatalogItemsSelection] = useState(false)

  const [enableCombosSelection, setEnableCombosSelection] = useState(false)

  useEffect(() => {
    handleGetProducts(() => {})
    handleGetCatalogItems(() => {})
    handleGetCombo(() => {})
  }, [])

  const options = useMemo(
    () => [
      { label: labels.allProducts, value: "all_products" },
      { label: labels.selectedProducts, value: "selected_products" },
      { label: labels.allCatalogItems, value: "all_catalog_items" },
      { label: labels.selectedCatalogItems, value: "selected_catalog_items" },
      { label: labels.allCombos, value: "all_combos" },
      { label: labels.selectedCombos, value: "selected_combos" },
    ],
    [labels],
  )

  const handleChangeTarget = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value as OptionValue
    setTargetType(value)

    if (value !== "selected_products") {
      setSelectedProductIds([])
    }

    if (value !== "selected_catalog_items") {
      setSelectedCatalogItemIds([])
    }

    if (value !== "selected_combos") {
      setSelectedComboIds([])
    }
  }

  const getRequest = (): ProductAssociationRequest => {
    switch (targetType) {
      case "selected_products":
        return {
          targetType: targetValues.selectedProducts,
          productIds: selectedProductIds,
          catalogItemIds: [],
          comboIds: [],
        }
      case "all_catalog_items":
        return { targetType: targetValues.allCatalogItems, productIds: [], catalogItemIds: [], comboIds: [] }
      case "selected_catalog_items":
        return {
          targetType: targetValues.selectedCatalogItems,
          productIds: [],
          catalogItemIds: selectedCatalogItemIds,
          comboIds: [],
        }
      case "all_combos":
        return { targetType: targetValues.allCombos, productIds: [], catalogItemIds: [], comboIds: [] }
      case "selected_combos":
        return { targetType: targetValues.selectedCombos, productIds: [], catalogItemIds: [], comboIds: selectedComboIds }
      default:
        return { targetType: targetValues.allProducts, productIds: [], catalogItemIds: [], comboIds: [] }
    }
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={title}
              icon={faSliders}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className="flex items-center gap-1">
                  <PopupHelpButton
                    helpKey={helpKey}
                    title={helpTitle}
                    variant="global"
                    description={helpDescription}
                    bullets={helpBullets}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey={interactionKeys.closeDescriptionKey}
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <div className="p-2">
              <div className="rounded-lg border border-orange-100 bg-orange-50/40 p-3">
                <GenericDropDown
                  legend={labels.targetLegend}
                  data={options}
                  initialState={targetType}
                  handlerFuncion={handleChangeTarget}
                  descriptionKey={interactionKeys.targetDescriptionKey}
                />
              </div>

              {targetType === "selected_products" && (
                <SelectionSection
                  title={labels.selectProductsAction}
                  countTitle={labels.selectedProductsCount}
                  emptyLabel={labels.noSelectedProducts}
                  selectedIds={selectedProductIds}
                  icon={faBoxOpen}
                  onAdd={() => setEnableProductsSelection(true)}
                  onRemove={(id) => setSelectedProductIds((current) => current.filter((itemId) => itemId !== id))}
                  addDescriptionKey={interactionKeys.addProductDescriptionKey}
                  removeDescriptionKey={interactionKeys.removeProductDescriptionKey}
                  getLabel={(id) => productsMemory.findById(id)?.catalogProductEntity.name ?? "-"}
                />
              )}

              {targetType === "selected_catalog_items" && (
                <SelectionSection
                  title={labels.selectCatalogItemsAction}
                  countTitle={labels.selectedCatalogItemsCount}
                  emptyLabel={labels.noSelectedCatalogItems}
                  selectedIds={selectedCatalogItemIds}
                  icon={faBoxOpen}
                  onAdd={() => setEnableCatalogItemsSelection(true)}
                  onRemove={(id) =>
                    setSelectedCatalogItemIds((current) => current.filter((itemId) => itemId !== id))
                  }
                  addDescriptionKey={interactionKeys.addCatalogItemDescriptionKey}
                  removeDescriptionKey={interactionKeys.removeCatalogItemDescriptionKey}
                  getLabel={(id) => catalogItemsMemory.findById(id)?.catalogProductEntity.name ?? "-"}
                />
              )}

              {targetType === "selected_combos" && (
                <SelectionSection
                  title={labels.selectCombosAction}
                  countTitle={labels.selectedCombosCount}
                  emptyLabel={labels.noSelectedCombos}
                  selectedIds={selectedComboIds}
                  icon={faLayerGroup}
                  onAdd={() => setEnableCombosSelection(true)}
                  onRemove={(id) => setSelectedComboIds((current) => current.filter((itemId) => itemId !== id))}
                  addDescriptionKey={interactionKeys.addComboDescriptionKey}
                  removeDescriptionKey={interactionKeys.removeComboDescriptionKey}
                  getLabel={(id) => comboMemory.findById(id)?.catalogProductEntity.name ?? "-"}
                />
              )}
            </div>

            <div className={defaultStyle.divisionBar}></div>

            <div className={defaultStyle.buttonsController}>
              <Button
                label={actionLabel}
                typeButton="button"
                type="primary"
                size="medium"
                bgColor={THEME.colors.buttonBGSaveColor}
                onClick={() => onConfirm(getRequest())}
                descriptionKey={interactionKeys.confirmDescriptionKey}
              />
            </div>
          </div>
        </Animate>
      </div>

      {enableProductsSelection && (
        <ListSelections
          title={labels.selectedProductsTitle}
          icon={faBoxOpen}
          listCards={productsMemory.getListCards(true)}
          messageCount={t("list:label_products_page_message_count")}
          messageEmpty={t("list:label_products_page_message_empty")}
          onClinck={(id) => {
            setSelectedProductIds((current) => (current.includes(id) ? current : [...current, id]))
            setEnableProductsSelection(false)
          }}
          onClonse={() => setEnableProductsSelection(false)}
        />
      )}

      {enableCatalogItemsSelection && (
        <ListSelections
          title={labels.selectedCatalogItemsTitle}
          icon={faBoxOpen}
          listCards={catalogItemsMemory.getListCards(true)}
          messageCount={t("list:label_catalogItems_page_message_count")}
          messageEmpty={t("list:label_catalogItems_page_message_empty")}
          onClinck={(id) => {
            setSelectedCatalogItemIds((current) => (current.includes(id) ? current : [...current, id]))
            setEnableCatalogItemsSelection(false)
          }}
          onClonse={() => setEnableCatalogItemsSelection(false)}
        />
      )}

      {enableCombosSelection && (
        <ListSelections
          title={labels.selectedCombosTitle}
          icon={faLayerGroup}
          listCards={comboMemory.getListCards(true)}
          messageCount={t("list:label_combo_page_message_count")}
          messageEmpty={t("list:label_combo_page_message_empty")}
          onClinck={(id) => {
            setSelectedComboIds((current) => (current.includes(id) ? current : [...current, id]))
            setEnableCombosSelection(false)
          }}
          onClonse={() => setEnableCombosSelection(false)}
        />
      )}
    </ModalPortal>
  )
}

interface SelectionSectionProps {
  title: string
  countTitle: string
  emptyLabel: string
  selectedIds: number[]
  icon: IconProp
  onAdd: () => void
  onRemove: (id: number) => void
  addDescriptionKey: string
  removeDescriptionKey: string
  getLabel: (id: number) => string
}

const SelectionSection: React.FC<SelectionSectionProps> = ({
  title,
  countTitle,
  emptyLabel,
  selectedIds,
  icon,
  onAdd,
  onRemove,
  addDescriptionKey,
  removeDescriptionKey,
  getLabel,
}) => {
  const { t } = useTranslation("buttons")

  return (
    <>
      <GetSpace top={10} />

      <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-orange-100 text-orange-600">
              <FontAwesomeIcon icon={icon} />
            </span>

            <span className="min-w-0 break-words text-sm font-semibold text-slate-700">{title}</span>
          </div>

          <ButtonIcon
            typeButton="button"
            type="primary"
            size="medium"
            icon={faPlus}
            sizeNumber={SIZE.buttonCloseIconSize}
            bgColor={THEME.colors.buttonBGPlusColor}
            iconColor={THEME.colors.buttonFGPlusColor}
            onClick={onAdd}
            descriptionKey={addDescriptionKey}
          />
        </div>

        <GetSpace top={5} />

        <CountElements count={selectedIds.length} title={countTitle} />

        <GetSpace top={5} />

        {selectedIds.length === 0 && <span className="text-xs text-slate-500">{emptyLabel}</span>}

        {selectedIds.map((id) => (
          <Animate key={id} type={AnimationTypes.fadeInTraslate} duration="1.2s">
            <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-700" title={getLabel(id)}>
                {getLabel(id)}
              </span>

              <button
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-50 transition-colors hover:bg-red-100"
                type="button"
                onClick={() => onRemove(id)}
                title={t("button_remove")}
                data-interaction-description-key={removeDescriptionKey}
              >
                <FontAwesomeIcon icon={faTrashCan} color={THEME.colors.buttonBGRemoveColor} />
              </button>
            </div>
          </Animate>
        ))}
      </div>
    </>
  )
}
